import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/AuthProvider";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { ArrowUpRight, Sparkles, RefreshCw, Loader2, MessageSquare } from "lucide-react";
import { toast } from "sonner";

const HERO = "https://images.unsplash.com/photo-1727341557146-4abab94d0812";
const DNA_JUNK = new Set(["", "undefined", "unknown", "n/a", "na", "none", "null"]);
const cleanDna = (v) =>
  typeof v === "string" && !DNA_JUNK.has(v.trim().toLowerCase()) ? v : null;

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const [items, setItems] = useState([]);
  const [outfits, setOutfits] = useState([]);
  const [completion, setCompletion] = useState(100);
  const [refreshingDna, setRefreshingDna] = useState(false);

  useEffect(() => {
    api.get("/wardrobe").then(({ data }) => setItems(data));
    api.get("/outfits").then(({ data }) => setOutfits(data));
    api.get("/users/me/completion").then(({ data }) => setCompletion(data.percent)).catch(() => {});
  }, []);

  const dna = user?.style_dna || {};
  const recommendedColors = (dna.recommended_colors || []).filter(
    (c) => typeof c === "string" && c.trim().toLowerCase() !== "teal"
  );
  const dnaLooksStale =
    !cleanDna(dna.body_shape) &&
    !cleanDna(dna.face_shape) &&
    !cleanDna(dna.skin_tone) &&
    !cleanDna(dna.undertone);

  const onRefreshDna = async () => {
    setRefreshingDna(true);
    try {
      await api.post("/users/me/style-dna/recompute");
      await refresh();
      toast.success("Style DNA refreshed.");
    } catch (e) {
      const msg = e?.response?.data?.detail?.message || e?.response?.data?.detail || "Couldn't refresh DNA.";
      toast.error(typeof msg === "string" ? msg : "Couldn't refresh DNA.");
    } finally {
      setRefreshingDna(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 lg:py-16">
      {completion < 80 && (
        <div className="mb-6 border border-border p-4 lg:p-5 bg-accent/10 flex items-center justify-between gap-4 flex-wrap" data-testid="completion-banner">
          <div>
            <p className="eyebrow text-accent">Profile · {completion}% complete</p>
            <p className="text-sm mt-1">Complete your profile to unlock the best styling experience.</p>
          </div>
          <Link to="/profile">
            <Button variant="outline" data-testid="complete-profile-cta" className="rounded-none uppercase tracking-[0.2em] text-[11px] h-10">Complete profile</Button>
          </Link>
        </div>
      )}

      <div className="grid lg:grid-cols-12 gap-8">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          className="lg:col-span-7 relative overflow-hidden border border-border"
        >
          <img src={HERO} alt="" className="h-[60vh] w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
            <p className="text-[11px] sm:text-[10px] uppercase tracking-[0.2em] sm:tracking-[0.3em] opacity-80">Today's Brief</p>
            <h1 className="font-serif text-4xl lg:text-5xl tracking-tight mt-3">
              Hello {user?.name?.split(" ")[0] || "darling"} —
              <br /><em className="italic opacity-90">what shall we wear?</em>
            </h1>
            <div className="mt-6 flex gap-3 flex-wrap">
              <Link to="/outfit">
                <Button className="rounded-none bg-white text-black hover:bg-white/90 uppercase tracking-[0.2em] text-[11px] h-11">
                  Generate today's look
                </Button>
              </Link>
              <Link to="/stylist">
                <Button variant="outline" data-testid="ask-stylist-btn" className="rounded-none bg-transparent text-white border-white/40 hover:bg-white/10 uppercase tracking-[0.2em] text-[11px] h-11">
                  Ask the AI stylist
                </Button>
              </Link>
            </div>
          </div>
        </motion.div>

        <div className="lg:col-span-5 grid gap-6">
          <div className="border border-border p-6" data-testid="dashboard-dna-card">
            <div className="flex items-start justify-between gap-3">
              <p className="eyebrow">Your Style DNA</p>
              <button
                type="button"
                onClick={onRefreshDna}
                disabled={refreshingDna}
                data-testid="dashboard-dna-refresh"
                title="Recompute from your latest profile & photo"
                className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 disabled:opacity-50"
              >
                {refreshingDna
                  ? <><Loader2 size={11} className="animate-spin" /> Refreshing</>
                  : <><RefreshCw size={11} /> Refresh</>}
              </button>
            </div>
            <p className="font-serif text-3xl mt-2 leading-tight">
              {cleanDna(dna.style_personality) || "Modern Minimalist"}
            </p>
            {dnaLooksStale && (
              <p className="text-[11px] text-muted-foreground mt-3 leading-relaxed">
                Add a clear face photo in <Link to="/profile/personal-details" className="underline underline-offset-2">Personal details</Link>, then tap <em>Refresh</em> — your traits will populate.
              </p>
            )}
            <div className="mt-5 grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <DnaRow label="Body" value={cleanDna(dna.body_shape)} />
              <DnaRow label="Face" value={cleanDna(dna.face_shape)} />
              <DnaRow label="Skin" value={cleanDna(dna.skin_tone)} />
              <DnaRow label="Undertone" value={cleanDna(dna.undertone)} />
            </div>
            <div className="mt-5 flex flex-wrap gap-1.5">
              {recommendedColors.slice(0, 6).map((c) => (
                <span key={c} className="px-2.5 py-1 border border-border text-[11px] sm:text-[10px] uppercase tracking-[0.14em] sm:tracking-[0.18em]">{c}</span>
              ))}
            </div>
          </div>

          <Link to="/tryon" className="border border-border p-6 group hover:border-foreground transition">
            <div className="flex items-start justify-between">
              <div>
                <p className="eyebrow">Try-On Studio</p>
                <p className="font-serif text-2xl mt-2">See it before you wear it.</p>
              </div>
              <ArrowUpRight className="group-hover:rotate-12 transition-transform" />
            </div>
          </Link>

          <Link to="/beauty" data-testid="dashboard-beauty-cta" className="border border-accent p-6 bg-accent text-accent-foreground group hover:opacity-95 transition">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-[0.25em] opacity-80">New · Beauty Studio</p>
                <p className="font-serif text-2xl mt-2">One scan. Makeup, hair &amp; care unlocked.</p>
              </div>
              <ArrowUpRight className="group-hover:rotate-12 transition-transform" />
            </div>
          </Link>
        </div>
      </div>

      <div className="mt-16 grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-4 border border-border p-6">
          <p className="eyebrow flex items-center gap-2"><Sparkles size={12} /> Wardrobe</p>
          <p className="font-serif text-5xl mt-3">{items.length}</p>
          <p className="text-sm text-muted-foreground mt-1">pieces curated · {items.filter(i => i.status==="available").length} available</p>
          <Link to="/wardrobe" className="mt-6 inline-block text-xs uppercase tracking-[0.2em] underline underline-offset-4">Manage closet</Link>
        </div>

        <div className="lg:col-span-4 border border-border p-6 bg-card">
          <p className="eyebrow text-accent">Coming soon</p>
          <p className="font-serif text-3xl mt-3 leading-tight">Stylist Marketplace</p>
          <p className="text-sm text-muted-foreground mt-2">Real stylists, real collections, real wardrobes you can shop.</p>
          <Link to="/coming-soon" className="mt-6 inline-block text-xs uppercase tracking-[0.2em] underline underline-offset-4">See what's next</Link>
        </div>

        <div className="lg:col-span-4 border border-accent p-6 bg-accent text-accent-foreground">
          <p className="text-[10px] uppercase tracking-[0.25em] opacity-80">Premium · {user?.subscription_tier === "free" ? "Upgrade" : "Active"}</p>
          <p className="font-serif text-3xl mt-3">Unlimited Try-Ons</p>
          <p className="text-sm opacity-80 mt-2">Stylist chat, family wardrobes, travel planning.</p>
          <Link to="/profile" className="mt-6 inline-block text-xs uppercase tracking-[0.2em] underline underline-offset-4">View plans</Link>
        </div>
      </div>

      {items.length > 0 && (
        <div className="mt-16">
          <div className="flex items-end justify-between">
            <h2 className="editorial-h2">Recent additions</h2>
            <Link to="/wardrobe" className="text-xs uppercase tracking-[0.2em] underline underline-offset-4">All pieces</Link>
          </div>
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {items.slice(0, 6).map((it) => (
              <div key={it.id} className="border border-border bg-card">
                <div className="aspect-square overflow-hidden bg-muted">
                  <img src={fileUrl(it.image_path)} alt={it.type} className="h-full w-full object-cover" />
                </div>
                <div className="p-3">
                  <p className="text-xs uppercase tracking-[0.18em] truncate">{it.type}</p>
                  <p className="text-[10px] text-muted-foreground mt-1">{it.color}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Feedback CTA — sits at the bottom of Home so it's discoverable but
          out of the primary editorial flow. */}
      <div className="mt-16 border-t border-border pt-8 flex justify-center">
        <Link
          to="/feedback?from=home"
          data-testid="home-feedback-btn"
          className="inline-flex items-center gap-2 border border-border px-6 h-11 text-[11px] uppercase tracking-[0.22em] hover:border-accent hover:text-accent transition-colors"
        >
          <MessageSquare size={13} /> Send feedback
        </Link>
      </div>
    </div>
  );
}

function DnaRow({ label, value }) {
  return (
    <div className="border-b border-border pb-2">
      <p className="text-[11px] sm:text-[10px] uppercase tracking-[0.16em] sm:tracking-[0.22em] text-muted-foreground">{label}</p>
      <p className="text-base sm:text-sm mt-1 sm:mt-0.5">{value || "—"}</p>
    </div>
  );
}
